import audio from '@/scripts/audio';
import eventHub from '@/scripts/eventHub';
import gsap from 'gsap';
import {
  Point,
  Sprite,
  Loader,
  AnimatedSprite,
  Texture,
} from 'pixi.js';
import store from '@/store';
import Smoke from '../emitters/Smoke';
import Tail from '../emitters/Tail';
import state from '../state';
import ticker from '../ticker';
import vars from '../vars';
import Actor from './Actor';

class Player extends Actor {
  constructor(parent) {
    super(parent);
    this.interaction = null;
    this.lerpedPosition = vars.layout.PLAYER_START_X;
    this.jumping = false;
    this.invincible = false;
    this.isButterfly = false;
    this.needEmission = { slowdown: true, speedup: false };
    this.mortalTimer = null;
    this.teaserTimer = null;
    this.distance = 0;

    this.sprite = new Sprite(Texture.EMPTY);
    // this.sprite.tint = 0xff0000;

    this.bubble = new AnimatedSprite(Object.values(Loader.shared.resources['sprites/BUBBLE'].textures));
    this.bubble.loop = false;
    this.bubble.stop();
    this.bubble.animationSpeed = 0.5;
    this.bubble.alpha = 0.6;
    this.bubble.textures = this.bubble.textures.reverse();
    this.bubble.gotoAndStop(this.bubble.totalFrames - 1);
    this.bubble.renderable = false;

    this.butterflyShield = new AnimatedSprite(Object.values(Loader.shared.resources['sprites/BUTTERFLY_SHIELD'].textures));
    this.butterflyShield.loop = false;
    this.butterflyShield.animationSpeed = 0.4;
    this.butterflyShieldBack = new AnimatedSprite(Object.values(Loader.shared.resources['sprites/BUTTERFLY_SHIELD_BACK'].textures));
    this.butterflyShieldBack.loop = false;
    this.butterflyShieldBack.animationSpeed = 0.4;
    this.butterflyIn = new AnimatedSprite(Object.values(Loader.shared.resources['sprites/BUTTERFLY_IN'].textures));
    this.butterflyIn.loop = false;
    this.butterflyIn.renderable = false;
    this.butterflyIn.animationSpeed = 0.4;
    this.butterfly = new AnimatedSprite(Object.values(Loader.shared.resources['sprites/BUTTERFLY'].textures));
    this.butterfly.loop = true;
    this.butterfly.renderable = false;
    this.butterfly.animationSpeed = 0.4;
    this.butterflyShadow = new Sprite(Loader.shared.resources['textures/BUTTERFLY_SHADOW'].texture);
    this.butterflyShadow.renderable = false;

    this.confetti = new AnimatedSprite(Object.values(Loader.shared.resources['sprites/CONFETTI_FULL'].textures));
    this.confetti.loop = false;
    this.confetti.stop();
    this.confetti.animationSpeed = 0.4;

    this.start = new AnimatedSprite(Object.values(Loader.shared.resources['sprites/START'].textures));
    this.start.loop = false;
    this.start.animationSpeed = 0.8;

    this.startLeafLeft = new Sprite(Loader.shared.resources['textures/START_LEAF_LEFT'].texture);
    this.startLeafRight = new Sprite(Loader.shared.resources['textures/START_LEAF_RIGHT'].texture);

    this.smoke = new Smoke(this.parent, this);

    this.parent.addChild(this.sprite);
    this.parent.addChild(this.confetti);
    this.parent.addChild(this.butterflyShadow);
    this.parent.addChild(this.butterflyIn);
    this.parent.addChild(this.butterfly);
    this.parent.addChild(this.butterflyShieldBack);
    this.parent.addChild(this.butterflyShield);
    this.parent.addChild(this.bubble);
    this.parent.addChild(this.start);
    // this.parent.addChild(this.startLeafRight);
    // this.parent.addChild(this.startLeafLeft);

    this.frameHandler = () => { this.frame(); };

    this.resize();
  }

  resize() {
    this.sprite.anchor = new Point(0.5, 0.5);
    this.sprite.width = 28;
    this.sprite.height = 28;
    this.sprite.position.x = vars.layout.PLAYER_START_X;
    this.sprite.position.y = vars.layout.PLAYER_START_Y;

    this.bubble.anchor = new Point(0.5, 0.5);
    this.bubble.position.y = this.sprite.position.y - 30;
    this.bubble.zIndex = 4;

    this.butterflyShield.anchor = new Point(0.5, 0.3);
    this.butterflyShield.position.y = this.sprite.position.y - 50;
    this.butterflyShield.zIndex = 4;
    this.butterflyShieldBack.anchor = new Point(0.5, 0.3);
    this.butterflyShieldBack.position.y = this.sprite.position.y - 50;
    this.butterflyShieldBack.zIndex = 3;
    this.butterflyIn.anchor = new Point(0.5, 0.5);
    this.butterflyIn.position.y = this.sprite.position.y - 50;
    this.butterflyIn.zIndex = 4;
    this.butterfly.anchor = new Point(0.5, 0.5);
    this.butterfly.position.y = this.sprite.position.y - 50;
    this.butterfly.zIndex = 4;
    this.butterflyShadow.anchor = new Point(0.5, 0.5);
    this.butterflyShadow.position.y = this.sprite.position.y - 50;

    this.confetti.anchor = new Point(0.5, 0.5);
    this.confetti.position.y = this.sprite.position.y - 15;
    this.confetti.zIndex = 4;
    this.confetti.onComplete = () => this.confetti.gotoAndStop(0);

    this.start.anchor = new Point(0.5, 1);
    this.start.position.x = vars.layout.PLAYER_START_X;
    this.start.position.y = vars.layout.PLAYER_START_Y;

    this.startLeafLeft.zIndex = 6;
    this.startLeafRight.zIndex = 6;
    this.startLeafLeft.anchor = new Point(0, 0.5);
    this.startLeafRight.anchor = new Point(1, 0.5);
    this.startLeafLeft.position.x = -80;
    this.startLeafRight.position.x = vars.layout.FRAME_WIDTH + 140;
    this.startLeafLeft.position.y = 90;
    this.startLeafRight.position.y = 130;
    this.startLeafLeft.scale = new Point(1.2, 1.5);
    this.startLeafRight.scale = new Point(1.2, 1.5);
  }

  frame() {
    // The horizontal movement
    this.position = this.interaction.point.x;
    this.lerpedPosition = state.delayFunction(this.position, this.lerpedPosition, state.delayFactor);
    this.sprite.position.x = this.lerpedPosition;
    this.bubble.position.x = this.sprite.position.x;
    this.butterflyShield.position.x = this.sprite.position.x;
    this.butterflyShieldBack.position.x = this.sprite.position.x;
    this.butterflyIn.position.x = this.sprite.position.x;
    this.butterfly.position.x = this.sprite.position.x;
    this.butterflyShadow.position.x = this.sprite.position.x + ((this.sprite.position.x - (vars.layout.FRAME_WIDTH / 2)) * 0.9);
    this.confetti.position.x = this.sprite.position.x;
    this.start.position.y -= state.speed * ticker.speed;

    const distance = this.sprite.position.x - (vars.layout.FRAME_WIDTH / 2);
    this.distance = distance;
    store.commit('SET_PLAYER_DISTANCE', this.distance);
    this.tail.shadow.rotation = distance * -0.03;
    this.tail.shadow.distance = distance * -0.03;
    this.butterfly.angle = distance * -0.2;
    this.butterflyShadow.angle = distance * -0.2;

    // After all decrease the fuel
    state.fuel -= state.fuelDecreaseSpeed;
    if (state.fuel <= 0 && this.needEmission.slowdown) {
      this.needEmission.speedup = true;
      this.needEmission.slowdown = false;
      state.speed = 1.5;
    } else if (this.needEmission.speedup) {
      this.needEmission.speedup = false;
      this.needEmission.slowdown = true;
      state.speed = state.initialSpeed;
    }
  }

  placeChar() {
    this.tail = new Tail(this.parent, this);
    gsap.to(this.tail.sprite.position, {
      y: 0,
      ease: 'power4.out',
      duration: 3,
    });
  }

  getReady(interaction) {
    this.interaction = interaction;
    ticker.add(this.frameHandler);
    this.smoke.startSpawn();
    this.start.play();
    // this.teaserTimer = setTimeout(() => this.checkTeaser(), 45 * 1000);
  }

  gameover() {
    clearInterval(this.teaserTimer);
    ticker.remove(this.frameHandler);
    this.sprite.renderable = false;
    this.tail.gameover();
    this.smoke.stopSpawn();
  }

  replay() {
    this.sprite.renderable = true;
    ticker.add(this.frameHandler);
    this.tail.replay();
    this.smoke.startSpawn();
    // this.teaserTimer = setTimeout(this.checkTeaser, 45 * 1000);
  }

  checkTeaser() {
    if (!window.gapp.purchased) {
      eventHub.$emit('gameover');
      eventHub.$emit('teaserend');
      audio.stop('background');
      this.start.gotoAndStop(0);
      this.setImmediatePosition(vars.layout.PLAYER_START_X, false);
    }
  }

  // Utils functions for external use

  setTemporaryInvincible(delay = 2000, playBubble = true) {
    if (playBubble) {
      this.bubble.textures = this.bubble.textures.reverse();
      this.bubble.renderable = true;
      this.bubble.onComplete = () => {};
      this.bubble.play();
    }
    this.invincible = true;
    this.setMortalTimer(delay, playBubble);
  }

  setMortalTimer(delay = 2000, playBubble) {
    if (this.mortalTimer) {
      clearTimeout(this.mortalTimer);
    }

    this.mortalTimer = setTimeout(() => {
      if (playBubble) {
        this.bubble.textures = this.bubble.textures.reverse();
        this.bubble.play();
        this.bubble.onComplete = () => { this.bubble.renderable = false; };
        state.hasPowerupOnStage = false;
      }
      this.invincible = false;
    }, delay);
  }

  setImmediatePosition(x = vars.layout.FRAME_WIDTH / 2, restart = true) {
    this.interaction.stop();
    this.sprite.position.x = x;
    this.lerpedPosition = x;
    this.tail.immediate = true;
    if (restart) this.interaction.start();
  }
}

export default Player;
